import { useCallback } from "react";
import type { Ref, RefCallback } from "react";

/**
 * Merge multiple React refs into a single callback ref.
 *
 * Supports both callback refs and RefObject instances.
 *
 * @example
 * const mergedRef = useMergeRefs(localRef, forwardedRef);
 * <div ref={mergedRef} />
 */
export function useMergeRefs<T>(...refs: (Ref<T> | undefined | null)[]): RefCallback<T> {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useCallback(
    (node: T | null) => {
      for (const ref of refs) {
        if (typeof ref === "function") {
          ref(node);
        } else if (ref != null) {
          (ref as React.MutableRefObject<T | null>).current = node;
        }
      }
    },
    // We intentionally spread refs as deps — the caller controls the identity of each ref.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    refs,
  );
}
